import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { CalibrationCertificateFilters, CalibrationStatus } from '../types';

export type CalibrationFilterKey = keyof CalibrationCertificateFilters;

const VALID_STATUSES: CalibrationStatus[] = ['overdue', 'due_soon', 'upcoming', 'later'];

/**
 * Parse comma-separated status string into array
 */
function parseStatusParam(param: string | null): CalibrationStatus[] {
  if (!param) return [];
  return param.split(',').filter((s): s is CalibrationStatus =>
    VALID_STATUSES.includes(s as CalibrationStatus)
  );
}

/**
 * Default filter values
 */
const DEFAULT_FILTERS: Omit<CalibrationCertificateFilters, 'status'> & { status: CalibrationStatus[] } = {
  status: [],
  dueInDays: 90,
  includeOverdue: true,
  pageNumber: 0,
  pageLength: 50,
  sortKey: 'due_date',
  sortDirection: 1,
};

/**
 * Hook for managing calibration certificate filters with URL synchronization
 * Filters are stored in the URL search params for easy sharing and bookmarking
 */
export function useCalibrationFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Parse filters from URL search params
   */
  const filters = useMemo<CalibrationCertificateFilters>(() => {
    const accountId = searchParams.get('accountId') || undefined;
    const search = searchParams.get('search') || undefined;
    const organisationId = searchParams.get('organisationId') || undefined;
    const statusArray = parseStatusParam(searchParams.get('status'));
    const dueDateFrom = searchParams.get('dueDateFrom') || undefined;
    const dueDateTo = searchParams.get('dueDateTo') || undefined;
    const pageNumber = parseInt(searchParams.get('pageNumber') || '0', 10);
    const pageLength = parseInt(searchParams.get('pageLength') || '50', 10);
    const sortKey = searchParams.get('sortKey') || DEFAULT_FILTERS.sortKey;
    const sortDirection = parseInt(searchParams.get('sortDirection') || '1', 10) as 1 | -1;

    // Calculate date for 'later' status filter (certificates due beyond 90 days)
    const hasLaterStatus = statusArray.includes('later');
    const laterStatusMinDate = (() => {
      const date = new Date();
      date.setDate(date.getDate() + 91);
      return date.toISOString().split('T')[0];
    })();

    // If 'later' is selected alone, we need special handling
    const onlyLaterSelected = statusArray.length === 1 && hasLaterStatus;

    return {
      accountId,
      search,
      organisationId,
      status: statusArray.length === 0 ? undefined : statusArray,
      dueInDays: onlyLaterSelected ? undefined : 90,
      dueDateFrom: dueDateFrom || (onlyLaterSelected ? laterStatusMinDate : undefined),
      dueDateTo: dueDateTo || undefined,
      includeOverdue: !onlyLaterSelected,
      pageNumber,
      pageLength,
      sortKey,
      sortDirection,
    };
  }, [searchParams]);

  /**
   * Get raw filter values for UI (before processing for API)
   */
  const rawFilters = useMemo(() => ({
    accountId: searchParams.get('accountId') || '',
    search: searchParams.get('search') || '',
    organisationId: searchParams.get('organisationId') || 'all',
    status: parseStatusParam(searchParams.get('status')),
    dueDateFrom: searchParams.get('dueDateFrom') || '',
    dueDateTo: searchParams.get('dueDateTo') || '',
    pageNumber: parseInt(searchParams.get('pageNumber') || '0', 10),
    pageLength: parseInt(searchParams.get('pageLength') || '50', 10),
    sortKey: searchParams.get('sortKey') || 'due_date',
    sortDirection: parseInt(searchParams.get('sortDirection') || '1', 10) as 1 | -1,
  }), [searchParams]);

  /**
   * Update a single filter value
   */
  const setFilter = useCallback(<K extends CalibrationFilterKey>(
    key: K,
    value: CalibrationCertificateFilters[K] | string | string[]
  ) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);

      // Handle special cases
      if (value === undefined || value === null || value === '' || value === 'all') {
        next.delete(key);
      } else if (Array.isArray(value)) {
        // Handle array values (e.g., status multi-select)
        if (value.length === 0) {
          next.delete(key);
        } else {
          next.set(key, value.join(','));
        }
      } else {
        next.set(key, String(value));
      }

      // Reset page when filters change (except for page changes)
      if (key !== 'pageNumber' && key !== 'pageLength') {
        next.delete('pageNumber');
      }

      return next;
    });
  }, [setSearchParams]);

  /**
   * Update multiple filter values at once
   */
  const setFilters = useCallback((newFilters: Partial<CalibrationCertificateFilters>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);

      Object.entries(newFilters).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '' || value === 'all') {
          next.delete(key);
        } else if (Array.isArray(value)) {
          if (value.length === 0) {
            next.delete(key);
          } else {
            next.set(key, value.join(','));
          }
        } else {
          next.set(key, String(value));
        }
      });

      // Reset page when filters change
      const hasNonPageChange = Object.keys(newFilters).some(
        (k) => k !== 'pageNumber' && k !== 'pageLength'
      );
      if (hasNonPageChange) {
        next.delete('pageNumber');
      }

      return next;
    });
  }, [setSearchParams]);

  /**
   * Reset all filters to defaults
   */
  const resetFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  /**
   * Load filters from a saved view
   */
  const loadFromSavedView = useCallback((viewFilters: Record<string, unknown>) => {
    setSearchParams(() => {
      const next = new URLSearchParams();

      Object.entries(viewFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          next.set(key, String(value));
        }
      });

      return next;
    });
  }, [setSearchParams]);

  /**
   * Get current filters as a plain object (for saving as a view)
   */
  const getFiltersForSave = useCallback((): Record<string, unknown> => {
    const result: Record<string, unknown> = {};

    searchParams.forEach((value, key) => {
      // Don't save pagination params or account-specific context
      if (key !== 'pageNumber' && key !== 'pageLength' && key !== 'accountId') {
        result[key] = value;
      }
    });

    return result;
  }, [searchParams]);

  /**
   * Check if any filters are active (non-default)
   */
  const hasActiveFilters = useMemo(() => {
    return Array.from(searchParams.keys()).some(
      (key) => key !== 'pageNumber' && key !== 'pageLength' && key !== 'sortKey' && key !== 'sortDirection'
    );
  }, [searchParams]);

  return {
    filters,
    rawFilters,
    setFilter,
    setFilters,
    resetFilters,
    loadFromSavedView,
    getFiltersForSave,
    hasActiveFilters,
  };
}
